// FichaDoLivro.js — as especificações do livro, em duas doses.
//
// ================= POR QUE DUAS PEÇAS (17/09/2026) =================
//
// As especificações moravam num bloco só, DEPOIS do hero: uma lista em três
// colunas de "Rótulo: valor" com ícones cinza, cortada em seis itens com um
// "Ver Mais". Era a parte que o dono apontou junto com os preços — e o
// problema era de lugar tanto quanto de forma. Quem está decidindo entre o
// e-book e o impresso quer saber QUANTAS PÁGINAS o livro tem na hora de olhar o
// preço, não três rolagens depois.
//
// Por isso a ficha virou duas peças, com o mesmo dado:
//
//   FichaRapida   três ou quatro números grandes (páginas, edição, idioma…),
//                 logo acima do cartão de preço. É o "de relance".
//   FichaTecnica  TODOS os itens, cada um num ladrilho com rótulo em cima e
//                 valor embaixo, na seção logo depois do hero. É a consulta.
//
// O "Ver Mais" saiu. Eram no máximo nove itens; esconder três deles atrás de um
// clique economizava uma fileira e custava a pessoa não achar o ISBN.
//
// OS DADOS CHEGAM PRONTOS (`itens`, montados em PaginaProduto a partir de
// META_DEFS): este arquivo só desenha. Rótulo e ícone continuam decididos lá,
// num lugar só.
import React, { useState } from "react";
import CabecalhoDeSecao from "./CabecalhoDeSecao";
import BotaoVerMais from "./BotaoVerMais";

/** Quantos itens a ficha técnica mostra no celular antes do "ver mais". */
const RECOLHIDA_NO_CELULAR = 4;

/**
 * Os itens que valem um número grande, na ordem em que aparecem.
 *
 * SÓ DADO CURTO. "Acabamento" e "Coloração" estiveram aqui, e o print da
 * página mostrou o resultado: "Brochura c/or…" cortado com reticências na
 * quarta casa. Um número grande só funciona com uma ou duas palavras; texto
 * longo mora na ficha técnica, onde ele quebra linha em vez de ser decepado.
 */
const DE_RELANCE = ["Nº de Páginas", "Edição", "Idioma"];

/** Rótulos curtos para a ficha rápida, onde o espaço é de uma palavra. */
const ROTULO_CURTO = {
  "Nº de Páginas": "páginas",
  "Edição": "edição",
  "Idioma": "idioma",
  "Acabamento": "acabamento",
  "Coloração": "cor",
};

/**
 * O valor como se LÊ, e não como está gravado.
 *
 * A edição chega do cadastro como "1", e a ficha dizia "1 · edição" — que se lê
 * como contagem, não como ordinal. Só número puro ganha o "ª": quem cadastrou
 * "2ª revista e ampliada" já escreveu do jeito certo e não pode virar "2ª ª".
 */
const valorExibido = (item) => {
  const bruto = String(item.value ?? "").trim();
  if (item.label === "Edição" && /^[0-9]+$/.test(bruto)) return bruto + "ª";
  // Parte do acervo foi cadastrada com o prefixo no próprio valor ("ISBN
  // 978-65-…"), e a ficha lia "ISBN / ISBN 978-65-…". O rótulo já diz o que é;
  // o valor fica só com o número — que é também o que o botão copia.
  if (item.label === "ISBN") return bruto.replace(/^ISBN[\s:.-]*/i, "");
  return bruto;
};

export function FichaRapida({ itens }) {
  const escolhidos = DE_RELANCE
    .map((rotulo) => itens.find((item) => item.label === rotulo))
    .filter(Boolean)
    .slice(0, 4);

  // Com um item só, a faixa vira um número isolado no meio do hero, sem
  // vizinhos que expliquem para que ela serve. Abaixo de dois, não aparece.
  if (escolhidos.length < 2) return null;

  return (
    <dl
      className="mt-6 grid overflow-hidden rounded-card border-2 border-insigne-sombra bg-insigne-navy-soft"
      // O número de colunas vem do número de itens: uma grade fixa de quatro
      // com dois itens deixaria meia faixa vazia à direita.
      style={{ gridTemplateColumns: `repeat(${escolhidos.length}, minmax(0, 1fr))` }}
    >
      {escolhidos.map((item, i) => (
        <div
          key={item.label}
          // `<dl>` aceita UM nível de <div> em volta de cada par dt/dd, e só
          // um: a caixa interna que existia aqui era HTML inválido. O
          // `flex-col-reverse` mora direto nela.
          //
          // NO CELULAR, MENOS RESPIRO E LETRA MENOR (17/09/2026): cada casa tem
          // ~112px a 390px, e com `px-3` (homônimo do Bootstrap: 1rem de cada
          // lado) e 18px de letra o print mostrou "Portugu…" cortado. `px-2.5`
          // não tem homônimo, e "Português" a 15px cabe inteiro.
          className={"flex min-w-0 flex-col-reverse px-2.5 py-3 sm:px-4 " + (i > 0 ? "border-l-2 border-insigne-sombra" : "")}
        >
          {/* <dt> embaixo do <dd> na tela, mas ANTES dele no DOM: o leitor de
              tela precisa ouvir "páginas: 368", e o número em cima é só visual. */}
          <dt className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-wide text-white/50 sm:text-[11px]">
            {ROTULO_CURTO[item.label] || item.label}
          </dt>
          <dd className="truncate text-[15px] font-extrabold leading-tight text-white sm:text-xl" title={valorExibido(item)}>
            {valorExibido(item)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** O ISBN ganha um botão de copiar: é o dado que se cola em outro lugar. */
const CopiarIsbn = ({ valor }) => {
  const [copiado, setCopiado] = useState(false);

  // Sem Clipboard API (http puro, navegador antigo), o botão simplesmente não
  // existe — melhor do que um botão que não faz nada.
  if (typeof navigator === "undefined" || !navigator.clipboard) return null;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(String(valor).trim());
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    } catch {
      /* permissão negada: nada a fazer, e nada a quebrar */
    }
  };

  return (
    <button
      type="button"
      onClick={copiar}
      aria-label={copiado ? "ISBN copiado" : "Copiar ISBN"}
      title={copiado ? "Copiado" : "Copiar ISBN"}
      className={
        "foco-laranja ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-controle border-2 transition-colors " +
        (copiado
          ? "border-insigne-promo text-insigne-promo"
          : "border-insigne-sombra text-white/50 hover:border-insigne-orange hover:text-insigne-orange")
      }
    >
      <i className={"bi text-xs " + (copiado ? "bi-check-lg" : "bi-copy")} aria-hidden="true" />
    </button>
  );
};

export function FichaTecnica({ itens }) {
  const [aberta, setAberta] = useState(false);
  if (!itens.length) return null;

  // ===== O QUE APARECE RECOLHIDO NO CELULAR (17/09/2026) =====
  //
  // Eram oito caixas empilhadas, ~660px de altura, a ficha mais longa da página
  // no celular. Recolhida, ela mostra quatro.
  //
  // E NÃO SÃO AS QUATRO PRIMEIRAS. Páginas, edição e idioma já estão na ficha
  // rápida, logo acima do preço; mostrá-los de novo aqui gastaria três das
  // quatro vagas repetindo o que a pessoa acabou de ler. As vagas vão primeiro
  // para o que só existe aqui (ISBN, formato, acabamento…). A ORDEM na tela não
  // muda — só quem fica escondido até o "ver mais".
  //
  // Se a ficha rápida NÃO apareceu (ela exige dois desses dados), os três não
  // estão em lugar nenhum e voltam a concorrer às vagas como qualquer outro.
  const rapidaAparece = DE_RELANCE.filter((r) => itens.some((i) => i.label === r)).length >= 2;
  const jaMostrado = (item) => rapidaAparece && DE_RELANCE.includes(item.label);
  const vagas = new Set(
    [...itens.filter((i) => !jaMostrado(i)), ...itens.filter(jaMostrado)]
      .slice(0, RECOLHIDA_NO_CELULAR)
      .map((i) => i.label)
  );
  const escondidos = itens.length - vagas.size;

  return (
    <section className="mb-12" aria-labelledby="ficha-tecnica">
      <CabecalhoDeSecao chapeu="Especificações" titulo="Ficha técnica" id="ficha-tecnica" />

      <dl className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {itens.map((item) => (
          <div
            key={item.label}
            // Fora das vagas, e com a ficha recolhida: some só abaixo de `sm`.
            // Do tablet para cima a grade tem duas ou três colunas e cabe inteira.
            className={
              "bloco min-w-0 items-center gap-3.5 px-3.5 py-3 sm:py-3.5 " +
              (!aberta && !vagas.has(item.label) ? "hidden sm:flex" : "flex")
            }
          >
            <span
              aria-hidden="true"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-controle
                         border-2 border-insigne-sombra bg-insigne-navy-deep text-lg text-insigne-orange"
            >
              <i className={"bi " + item.icon} />
            </span>
            <div className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-wide text-white/50">{item.label}</dt>
              {/* `break-words`, e não `truncate`: aqui é a consulta, e um ISBN
                  ou um formato cortado com reticências é justamente o dado que
                  a pessoa veio ler inteiro. */}
              <dd className="break-words text-[15px] font-bold leading-snug text-white">{valorExibido(item)}</dd>
            </div>
            {item.label === "ISBN" && <CopiarIsbn valor={valorExibido(item)} />}
          </div>
        ))}
      </dl>

      {escondidos > 0 && (
        <BotaoVerMais
          aberto={aberta}
          aoAlternar={() => setAberta((v) => !v)}
          textoFechado={`Ver ficha completa (+${escondidos})`}
          className="sm:hidden"
        />
      )}
    </section>
  );
}
